import React, { createContext, useContext, useState, useCallback } from 'react';

// Section types for contractor dashboard
export type DashboardSection = 'today' | 'requests' | 'messages' | 'invoices' | 'history' | 'calendar' | 'earnings' | 'settings' | 'bugs';

// Notification type to section mapping
export const notificationTypeToSection: Record<string, DashboardSection> = {
  // Booking lifecycle -> Requests or Calendar
  BOOKING_REQUESTED: 'requests',
  BOOKING_CONFIRMED: 'calendar',
  BOOKING_CANCELLED: 'history',
  BOOKING_REMINDER: 'today',

  // Day-based booking flow
  TIME_OPTIONS_PROPOSED: 'requests',
  TIME_OPTION_SELECTED: 'calendar',
  TIME_OPTIONS_DECLINED: 'requests',

  // Reschedule flow
  RESCHEDULE_PROPOSED: 'requests',
  RESCHEDULE_ACCEPTED: 'calendar',
  RESCHEDULE_DECLINED: 'requests',

  // Invoice/Payment
  INVOICE_SENT: 'invoices',
  PAYMENT_RECEIVED: 'invoices',
  PAYMENT_FAILED: 'invoices',

  // Messaging
  NEW_MESSAGE: 'messages',

  // System
  WELCOME: 'today',
  ACCOUNT_UPDATE: 'settings',
};

interface DashboardNavigationContextType {
  navigateToSection: (section: DashboardSection, entityId?: number) => void;
  registerNavigationHandler: (handler: (section: DashboardSection, entityId?: number) => void) => void;
  currentSection: DashboardSection | null;
  pendingEntityId: number | null;
}

const DashboardNavigationContext = createContext<DashboardNavigationContextType | null>(null);

export const DashboardNavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [navigationHandler, setNavigationHandler] = useState<((section: DashboardSection, entityId?: number) => void) | null>(null);
  const [currentSection, setCurrentSection] = useState<DashboardSection | null>(null);
  const [pendingEntityId, setPendingEntityId] = useState<number | null>(null);

  const registerNavigationHandler = useCallback((handler: (section: DashboardSection, entityId?: number) => void) => {
    setNavigationHandler(() => handler);
  }, []);

  const navigateToSection = useCallback((section: DashboardSection, entityId?: number) => {
    setCurrentSection(section);
    setPendingEntityId(entityId || null);

    if (navigationHandler) {
      navigationHandler(section, entityId);
    }
  }, [navigationHandler]);

  return (
    <DashboardNavigationContext.Provider value={{
      navigateToSection,
      registerNavigationHandler,
      currentSection,
      pendingEntityId
    }}>
      {children}
    </DashboardNavigationContext.Provider>
  );
};

export const useDashboardNavigation = () => {
  const context = useContext(DashboardNavigationContext);
  if (!context) {
    // Return a no-op version if used outside the provider (e.g., in ClientDashboard)
    return {
      navigateToSection: () => {},
      registerNavigationHandler: () => {},
      currentSection: null,
      pendingEntityId: null
    };
  }
  return context;
};

export default DashboardNavigationContext;
